import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Cache profile data to prevent unnecessary requests
  const profileCacheRef = React.useRef(new Map());
  const loadingUserRef = React.useRef(null); // Track which user is being loaded

  // Move useRef outside of useEffect
  const lastProcessedTokenRef = React.useRef(null);

  useEffect(() => {
    // When other parts of the app update the profile, keep AuthContext in sync
    // so route guards don't redirect based on stale data.
    const handler = (event) => {
      const detail = event?.detail || {};
      const userId = detail.userId;
      const profile = detail.profile;

      if (!userId || !profile) return;

      // Update caches
      profileCacheRef.current.set(userId, profile);
      try {
        const cacheData = { ...profile, _cacheTimestamp: Date.now() };
        localStorage.setItem(`profile_${userId}`, JSON.stringify(cacheData));
      } catch (e) {
        // Non-fatal
      }

      // Update live state if it matches the current user
      setUser((prev) => {
        if (!prev || prev.id !== userId) return prev;
        return { ...prev, profile };
      });
    };

    window.addEventListener('quotetier:profile-updated', handler);
    return () => window.removeEventListener('quotetier:profile-updated', handler);
  }, []);

  useEffect(() => {
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      
      // CRITICAL: Use setTimeout to prevent Supabase deadlock 
      // Official Supabase docs: "Do not use other Supabase functions in the callback"
      setTimeout(async () => {
        // Deduplicate repeated SIGNED_IN events with same access token
        if (event === 'SIGNED_IN' && session?.access_token) {
          if (lastProcessedTokenRef.current === session.access_token) {
            return;
          }
          lastProcessedTokenRef.current = session.access_token;
        }
        
        if (event === 'INITIAL_SESSION') {
          // Handle initial session
          if (session?.user) {
            // Check in-memory cache first
            const cachedProfile = profileCacheRef.current.get(session.user.id);
            if (cachedProfile) {
              setUser({ ...session.user, profile: cachedProfile });
              setLoading(false);
            } else {
              // Check localStorage cache as fallback
              try {
                const localCacheKey = `profile_${session.user.id}`;
                const localProfile = localStorage.getItem(localCacheKey);
                if (localProfile) {
                  const parsedProfile = JSON.parse(localProfile);
                  // Check if cache is less than 5 minutes old
                  if (parsedProfile._cacheTimestamp && Date.now() - parsedProfile._cacheTimestamp < 5 * 60 * 1000) {
                    delete parsedProfile._cacheTimestamp; // Remove metadata
                    profileCacheRef.current.set(session.user.id, parsedProfile);
                    setUser({ ...session.user, profile: parsedProfile });
                    setLoading(false);
                    return;
                  }
                }
              } catch (e) {
              }
              
              await loadUserProfile(session.user);
            }
          } else {
            setLoading(false);
          }
        } else if (event === 'SIGNED_IN' && session?.user) {
          await loadUserProfile(session.user);
          
          // Clear OAuth completion flag
          localStorage.removeItem('oauthJustCompleted');
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setLoading(false);
          loadingUserRef.current = null;
          lastProcessedTokenRef.current = null;
          
          // Clear all cached profiles
          profileCacheRef.current.clear();
          
          // Clear localStorage cache
          try {
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith('profile_')) {
                localStorage.removeItem(key);
              }
            });
          } catch (e) {
          }
        }
      }, 0); // setTimeout with 0ms prevents Supabase deadlock
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserProfile = async (authUser) => {
    // Prevent duplicate loading for the same user
    if (loadingUserRef.current === authUser.id) {
      return;
    }
    
    loadingUserRef.current = authUser.id;
    
    try {
      setProfileLoading(true);
      
      // Check cache first and use as fallback
      const localCacheKey = `profile_${authUser.id}`;
      let cachedProfile = null;
      try {
        const localProfile = localStorage.getItem(localCacheKey);
        if (localProfile) {
          const parsedProfile = JSON.parse(localProfile);
          if (parsedProfile._cacheTimestamp) {
            delete parsedProfile._cacheTimestamp;
            cachedProfile = parsedProfile;
          }
        }
      } catch (e) {
        // Silent fail
      }
      
      // Optimized query
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id, full_name, business_name, onboarding_completed, trial_setup_completed, subscription_status')
        .eq('id', authUser.id)
        .single();
      
      if (error) {
        
        // Only create profile for genuine "no rows" error (PGRST116)
        if (error.code === 'PGRST116' || error.message.includes('No rows returned')) {
          try {
            const { data: newProfile, error: createError } = await supabase
              .from('profiles')
              .insert({
                id: authUser.id,
                onboarding_completed: false,
                trial_setup_completed: false
              })
              .select()
              .single();
            
            if (createError) {
              // Use cached profile as fallback
              const fallbackProfile = cachedProfile || {
                id: authUser.id,
                onboarding_completed: false,
                trial_setup_completed: false
              };
              setUser({ ...authUser, profile: fallbackProfile });
            } else {
              // Cache the new profile
              profileCacheRef.current.set(authUser.id, newProfile);
              try {
                const cacheData = { ...newProfile, _cacheTimestamp: Date.now() };
                localStorage.setItem(localCacheKey, JSON.stringify(cacheData));
              } catch (e) {
              }
              setUser({ ...authUser, profile: newProfile });
            }
          } catch (createErr) {
            // Use cached profile as fallback
            const fallbackProfile = cachedProfile || {
              id: authUser.id,
              onboarding_completed: false,
              trial_setup_completed: false
            };
            setUser({ ...authUser, profile: fallbackProfile });
          }
        } else {
          // Network error, timeout, etc. - use cached profile and continue
          const fallbackProfile = cachedProfile || {
            id: authUser.id,
            onboarding_completed: false,
            trial_setup_completed: false
          };
          setUser({ ...authUser, profile: fallbackProfile });
          
          // Schedule retry in background
          setTimeout(() => {
            loadUserProfile(authUser);
          }, 2000);
        }
      } else {
        // Cache the profile in memory and localStorage
        profileCacheRef.current.set(authUser.id, profile);
        
        // Cache to localStorage with timestamp
        try {
          const cacheData = { ...profile, _cacheTimestamp: Date.now() };
          localStorage.setItem(`profile_${authUser.id}`, JSON.stringify(cacheData));
        } catch (e) {
        }
        
        setUser({ ...authUser, profile });
      }
    } catch (error) {
      // Use cached profile as final fallback
      const fallbackProfile = cachedProfile || {
        id: authUser.id,
        onboarding_completed: false,
        trial_setup_completed: false
      };
      setUser({ ...authUser, profile: fallbackProfile });
    } finally {
      setProfileLoading(false);
      setLoading(false);
      loadingUserRef.current = null; // Clear loading ref
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      return data;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    profileLoading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
